import { fhirClient } from '../../../util/api';
import { isCurrentInvalidFileDocumentReference } from './fhir';
import type { InvalidFileDocumentReference } from './types';

export type DocumentUpdateConfirmationResult =
  | {
      status: 'confirmed';
    }
  | {
      status: 'timeout';
    }
  | {
      status: 'error';
      error: unknown;
    };

type FetchDocumentReference = (
  documentReferenceId: string,
) => Promise<InvalidFileDocumentReference | undefined>;

const sleep = (ms: number) =>
  new Promise(resolve => {
    window.setTimeout(resolve, ms);
  });

const isNotFoundError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export const fetchDocumentReferenceForConfirmation: FetchDocumentReference =
  async documentReferenceId => {
    try {
      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/DocumentReference/:id' as never,
        { id: documentReferenceId } as never,
      );

      return response.data as InvalidFileDocumentReference;
    } catch (error) {
      if (isNotFoundError(error)) {
        return undefined;
      }

      throw error;
    }
  };

export const isDocumentUpdateConfirmed = (
  documentReference: InvalidFileDocumentReference | undefined,
) =>
  !documentReference ||
  !isCurrentInvalidFileDocumentReference(documentReference);

export const waitForDocumentUpdateConfirmation = async ({
  documentReferenceId,
  timeoutMs = 30000,
  intervalMs = 1000,
  fetchDocumentReference = fetchDocumentReferenceForConfirmation,
}: {
  documentReferenceId: string;
  timeoutMs?: number;
  intervalMs?: number;
  fetchDocumentReference?: FetchDocumentReference;
}): Promise<DocumentUpdateConfirmationResult> => {
  const startedAt = Date.now();

  try {
    while (Date.now() - startedAt <= timeoutMs) {
      const documentReference = await fetchDocumentReference(
        documentReferenceId,
      );

      if (isDocumentUpdateConfirmed(documentReference)) {
        return { status: 'confirmed' };
      }

      await sleep(intervalMs);
    }

    return { status: 'timeout' };
  } catch (error) {
    return { status: 'error', error };
  }
};
